/**
 * @brisa/shared/observability/sentry-config
 *
 * initSentry + beforeSend scrubber — ADR-009 §9.7.
 * beforeSend aplica PII scrubber do logger + redacta breadcrumbs /journal/*.
 */
import { scrubPII } from '../logger/scrubber';

// Tipo local para evitar dependência direta do Sentry SDK em testes.
// Em produção, @sentry/nextjs é o consumer real.
interface SentryBreadcrumb {
  category?: string;
  data?: { to?: string; from?: string; url?: string; [k: string]: unknown };
}

interface SentryEvent {
  extra?: Record<string, unknown>;
  request?: { headers?: Record<string, unknown> };
  breadcrumbs?: SentryBreadcrumb[];
  [k: string]: unknown;
}

const JOURNAL_URL_REGEX = /\/journal\b/i;

/**
 * Sentry beforeSend hook — aplicado a todo evento antes de ser enviado.
 */
export function sentryBeforeSend(event: SentryEvent): SentryEvent | null {
  if (!event) return event;

  const out: SentryEvent = { ...event };

  if (out.extra) {
    out.extra = scrubPII(out.extra) as Record<string, unknown>;
  }

  if (out.request?.headers) {
    out.request = {
      ...out.request,
      headers: scrubPII(out.request.headers) as Record<string, unknown>,
    };
  }

  if (Array.isArray(out.breadcrumbs)) {
    out.breadcrumbs = out.breadcrumbs.map((b) => {
      const data = b.data ?? {};
      const to = typeof data.to === 'string' ? data.to : undefined;
      const url = typeof data.url === 'string' ? data.url : undefined;
      const needsRedact = (to && JOURNAL_URL_REGEX.test(to)) || (url && JOURNAL_URL_REGEX.test(url));
      if (!needsRedact) return b;
      const newData: typeof data = { ...data };
      if (to && JOURNAL_URL_REGEX.test(to)) newData.to = '[REDACTED journal route]';
      if (url && JOURNAL_URL_REGEX.test(url)) newData.url = '[REDACTED journal route]';
      return { ...b, data: newData };
    });
  }

  return out;
}

/**
 * Inicializa Sentry — chamado em instrumentation.ts (server/edge) e em
 * sentry.client.config.ts (browser).
 *
 * Stub-safe: em ambiente sem Sentry SDK disponível, no-op.
 */
export async function initSentry(): Promise<void> {
  try {
    const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
    if (!dsn) return;
    const mod = (await import('@sentry/nextjs').catch(() => null)) as
      | { init?: (opts: Record<string, unknown>) => void }
      | null;
    mod?.init?.({
      dsn,
      environment: process.env.BRISA_ENV ?? 'dev',
      tracesSampleRate: 0.1,
      beforeSend: sentryBeforeSend,
    });
  } catch {
    /* swallow — never break boot */
  }
}
